import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

import {useDerivedProduct} from '../../../hooks/use-derived-product'
import {
    useProductAvailability,
    QUICK_ADD_AVAILABILITY_STALE_TIME
} from '../../../hooks/use-product-availability'
import {isVariantValueOrderable} from '../../../hooks/use-variation-attributes'
import {
    getPriceData,
    isEnrichedSearchHit,
    buildVariationAttributesFromHit
} from '../../../utils/product-utils'
import {
    getQVSelections,
    setQVSelections,
    clearQVSelections
} from '../../../utils/quick-view-selection-storage'

const findVariantByProductId = (product, productId) =>
    product?.variants?.find((variant) => variant?.productId === productId)

const useQuickViewProductData = ({
    productId,
    isProductPartOfSet,
    isProductPartOfBundle,
    setColorValue,
    setProductId,
    searchHitData,
    onVariantSelect,
    onProductChange,
    onLocalSelectionsChange,
    intl
}) => {
    const [pid, setPid] = useState(productId)

    // The PLP search hit already carries variants, variationGroups, imageGroups and
    // per-variant tieredPrices — enough to paint instantly. It keys by `productId`
    // (no top-level `id`) and lacks the decorated `variationAttributes` array, so we
    // normalize the id and let the structure call below fill in the swatches.
    const seed = useMemo(() => {
        if (pid !== productId || !isEnrichedSearchHit(searchHitData)) return undefined
        const hitId = searchHitData.id || searchHitData.productId
        // Hit has no decorated variationAttributes — derive them for instant swatches.
        const variationAttributes = searchHitData.variationAttributes?.length
            ? searchHitData.variationAttributes
            : buildVariationAttributesFromHit(searchHitData)
        // Resolve the group's color so the size filter keeps the sizes.
        const seedColor =
            searchHitData.variationValues?.color ||
            searchHitData.variationGroups?.find((vg) => vg.productId === hitId)?.variationValues
                ?.color ||
            searchHitData.representedProduct?.c_color ||
            searchHitData.c_color
        return {
            ...searchHitData,
            id: hitId,
            variationAttributes,
            variationValues: seedColor
                ? {...searchHitData.variationValues, color: seedColor}
                : searchHitData.variationValues
        }
    }, [pid, productId, searchHitData])

    const hasHitImages =
        Array.isArray(searchHitData?.imageGroups) && searchHitData.imageGroups.length > 0

    // Product structure (variants/variationGroups/images). Prices/promotions/images come from
    // the cached hit when present; otherwise this call also pulls them. Seeded so the card paints
    // instantly. Live orderable/inventory is fetched separately via the Shopper Availability API.
    const {
        data: productData,
        isLoading: isProductLoading,
        isPlaceholderData
    } = useProduct(
        {
            parameters: {
                id: pid,
                expand: hasHitImages
                    ? ['variations']
                    : ['variations', 'prices', 'promotions', 'images'],
                ...(hasHitImages ? {} : {allImages: true, perPricebook: true})
            }
        },
        {
            keepPreviousData: true,
            placeholderData: seed
        }
    )

    // Lean, real-time availability (orderable/inventory) joined on the variant productId.
    //
    // Request by the MASTER id (when present) with expand=variations so a SINGLE call returns
    // fresh orderable for the opened product AND every sibling size in one round-trip. The
    // Shopper Availability API only expands a MASTER id — a variant or variation-group requested
    // by its own id returns just that one entry (no siblings). Cart-edit opens the Quick View by
    // a specific VARIANT id, so requesting by `pid` there would never refresh the sibling sizes
    // and would leave the selected variant on its stale product-document `orderable` (wrongly
    // shown as out of stock -> "Shop Similar"). Falls back to the product id for master/standalone
    // products (which already expand correctly). Gated on the real product being loaded (not the
    // instant seed) so the master id is known and the request id is stable = one round-trip.
    const availabilityRequestId = productData?.master?.masterId || productData?.id || pid
    const {
        data: availabilityMap,
        isFetched: isAvailabilityFetched,
        isFetching: isAvailabilityFetching
    } = useProductAvailability(availabilityRequestId, {
        expandVariations: true,
        enabled: Boolean(productData) && !isPlaceholderData,
        staleTime: QUICK_ADD_AVAILABILITY_STALE_TIME
    })

    // Overlay the hit's prices/images and fresh availability onto the product response.
    const product = useMemo(() => {
        if (!productData) return productData
        const availability = availabilityMap?.[availabilityRequestId]
        const variantOrderableMap = new Map(
            availability?.variants?.map((v) => [v.productId, v.orderable]) || []
        )
        const hitByPid = new Map((searchHitData?.variants || []).map((v) => [v.productId, v]))
        const variants = (productData.variants || []).map((variant) => {
            const hit = hitByPid.get(variant.productId)
            const orderable = variantOrderableMap.has(variant.productId)
                ? variantOrderableMap.get(variant.productId)
                : variant.orderable
            if (!hit) return {...variant, orderable}
            return {
                ...variant,
                orderable,
                price: variant.price ?? hit.price,
                tieredPrices: variant.tieredPrices ?? hit.tieredPrices
            }
        })
        // Top-level inventory must reflect the OPENED product itself. When we requested the
        // master, the opened product appears among the expanded variant entries; otherwise it is
        // the requested entry directly.
        const selfInventory =
            availabilityRequestId === productData.id
                ? availability?.inventory
                : availability?.variants?.find((v) => v.productId === productData.id)?.inventory ||
                  availability?.inventory
        return {
            ...productData,
            variants,
            inventory: selfInventory || productData.inventory,
            imageGroups: productData.imageGroups || searchHitData?.imageGroups,
            productPromotions: productData.productPromotions || searchHitData?.productPromotions
        }
    }, [productData, availabilityMap, searchHitData, availabilityRequestId])

    const isVariantDataStale = Boolean(product && product.id !== pid)
    const isVariationDataLoading = isProductLoading || isVariantDataStale

    // Whole-card skeleton only when there's no product to show yet (seeded → instant).
    const showSkeleton = !product

    // Real response in (not the seed) — lets the action button hold its label until ready.
    const isAvailabilityResolved = Boolean(product) && !isPlaceholderData && !isVariantDataStale

    // Hold the availability-dependent UI (variation selectors + action CTA) in a loading state
    // until the interface is FULLY ready for this product — i.e. the real product structure has
    // resolved (not the instant seed) AND the Shopper Availability request has settled
    // (completed at least once AND not re-fetching). Revealing on any one of these alone lets
    // the interface paint with seed/partial/stale data and then visibly update a moment later
    // ("seeing the interface before it's completely updated"). Requiring all of them means it
    // reveals once, fully resolved. On a color change `pid` changes → product goes stale + the
    // availability query key resets → pending again with no gap.
    const isAvailabilityPending =
        Boolean(pid) &&
        (!isAvailabilityResolved || !isAvailabilityFetched || isAvailabilityFetching)

    const isWishlistEnabled = product?.c_enableWishlistCount || false
    const variants = product?.variationGroups

    // Track user selections locally (since QV doesn't use URL params like PDP)
    // This state persists user's size/length selections
    const [localSelections, setLocalSelections] = useState(() =>
        getQVSelections(productId)
    )
    const forceResetSelectionsRef = useRef(false)
    const preserveSelectionsOnPidChangeRef = useRef(false)

    useEffect(() => {
        if (productId && productId !== pid) {
            setPid(productId)
            // Restore cached selections for this product (if any)
            setLocalSelections(getQVSelections(productId))
            preserveSelectionsOnPidChangeRef.current = true
        }
    }, [productId, pid])

    useEffect(() => {
        const cacheKey = productId || pid
        if (!cacheKey) return
        if (Object.keys(localSelections).length > 0) return

        const cachedSelections = getQVSelections(cacheKey)
        if (Object.keys(cachedSelections).length === 0) return

        setLocalSelections(cachedSelections)
        preserveSelectionsOnPidChangeRef.current = true
    }, [productId, pid, localSelections])

    // Track previous pid to detect color changes and reset non-color selections
    const prevPidRef = useRef(pid)

    // Get base variation data from hook
    const {
        quantity,
        variationParams: derivedParams,
        variationAttributes: baseVariationAttributes,
        variant
    } = useDerivedProduct(
        product,
        isProductPartOfSet,
        isProductPartOfBundle,
        false // useParams=false to avoid URL contamination
    )

    // Reset non-color local selections when pid changes (color change)
    // This fixes the timing issue where stale size/length persisted after color change
    useEffect(() => {
        if (prevPidRef.current !== pid && prevPidRef.current !== null) {
            if (forceResetSelectionsRef.current) {
                forceResetSelectionsRef.current = false
                setLocalSelections({})
                prevPidRef.current = pid
                return
            }
            if (preserveSelectionsOnPidChangeRef.current) {
                preserveSelectionsOnPidChangeRef.current = false
                prevPidRef.current = pid
                return
            }
            // Product changed (color change) - clear size/length selections
            // Keep color in sync but clear other selections
            setLocalSelections((prev) => {
                const newSelections = {}
                // Only preserve color selection if it exists
                if (prev.color) {
                    newSelections.color = prev.color
                }
                return newSelections
            })
        }
        prevPidRef.current = pid
    }, [pid])

    useEffect(() => {
        const cacheKey = productId || pid
        if (!cacheKey) return
        setQVSelections(cacheKey, localSelections)
    }, [productId, pid, localSelections])

    // Merge local selections with derived params (local selections take precedence)
    const variationParams = useMemo(() => {
        return {...derivedParams, ...localSelections}
    }, [derivedParams, localSelections])

    // Update variationAttributes with merged variationParams to reflect user selections
    // Also recalculate orderable for each value based on current selections (like PDP does)
    const variationAttributes = useMemo(() => {
        return baseVariationAttributes.map((attr) => ({
            ...attr,
            selectedValue: {
                name: attr.values?.find((v) => v.value === variationParams?.[attr.id])?.name,
                value: variationParams?.[attr.id]
            },
            // Recalculate orderable for each value based on current selections
            values: attr.values?.map((val) => {
                // Skip orderable recalculation for color (uses variation groups)
                if (attr.id === 'color') {
                    return val
                }

                // Build params with current selections + this value
                const orderableParams = {
                    ...localSelections,
                    [attr.id]: val.value
                }

                // Only recalculate if we have at least one selection
                if (!isVariationDataLoading && Object.keys(localSelections).length > 0) {
                    const calculatedOrderable = isVariantValueOrderable(product, orderableParams)

                    // The base `orderable` from the product structure is authoritative for
                    // greying and reflects real availability. The per-combination recalc may
                    // only make a value MORE restrictive (e.g. a size that is out of stock for
                    // the currently selected length); it must NEVER upgrade a value already
                    // marked unavailable back to available. Without this, switching to another
                    // variant (size) recomputes from variant-level data and wrongly clears the
                    // greyed-out state on sizes that are genuinely out of stock.
                    if (calculatedOrderable === false) {
                        return {
                            ...val,
                            orderable: false
                        }
                    }
                }

                return val
            })
        }))
    }, [baseVariationAttributes, variationParams, localSelections, product, isVariationDataLoading])

    const selectedVariant = useMemo(() => {
        if (!product?.variants || !variationAttributes?.length) return null
        const allAttributeIds = variationAttributes.map((attr) => attr.id)
        const allSelected = allAttributeIds.every((id) => variationParams?.[id])
        if (!allSelected) return null

        return product.variants.find((v) =>
            allAttributeIds.every((id) => v.variationValues?.[id] === variationParams[id])
        )
    }, [product?.variants, variationAttributes, variationParams])

    const effectiveVariant = selectedVariant || variant

    const priceData = useMemo(() => {
        if (effectiveVariant?.productId) {
            const selectedProductVariant =
                findVariantByProductId(product, effectiveVariant.productId) || effectiveVariant
            const selectedSearchVariant = findVariantByProductId(
                searchHitData,
                effectiveVariant.productId
            )

            const variantWithPromotions = {
                ...selectedSearchVariant,
                ...selectedProductVariant,
                // Prefer whichever side has the price so the price-less call doesn't blank it.
                price: selectedProductVariant?.price ?? selectedSearchVariant?.price,
                tieredPrices:
                    selectedProductVariant?.tieredPrices ?? selectedSearchVariant?.tieredPrices,
                productPromotions:
                    selectedProductVariant?.productPromotions ||
                    selectedSearchVariant?.productPromotions ||
                    product?.productPromotions ||
                    searchHitData?.productPromotions,
                c_newArrivalDate:
                    selectedProductVariant?.c_newArrivalDate ||
                    selectedSearchVariant?.c_newArrivalDate ||
                    product?.c_newArrivalDate ||
                    searchHitData?.c_newArrivalDate
            }

            const variantPriceData = getPriceData(variantWithPromotions, {quantity})
            // Fall back to the hit's group price if the variant has none.
            if (!variantPriceData?.currentPrice) {
                return getPriceData(searchHitData || product, {quantity})
            }
            return variantPriceData
        }

        // No variant selected: price from the hit (the call no longer returns prices).
        return getPriceData(searchHitData || product, {quantity})
    }, [product, searchHitData, effectiveVariant?.productId, quantity])

    const [showOptionsMessage, setShowOptionsMessage] = useState(false)

    // Notify parent (ProductTile) when variant selection changes
    // This allows the tile price to update based on selected size
    useEffect(() => {
        if (onVariantSelect) {
            onVariantSelect(effectiveVariant?.productId || null)
        }
    }, [effectiveVariant?.productId, onVariantSelect])

    // Notify parent (ProductTile) when swatch changes
    useEffect(() => {
        if (product && onProductChange) {
            onProductChange(product || null)
        }
    }, [product, onProductChange])

    const hasUnselectedVariations = useMemo(() => {
        return variationAttributes.some((attr) => !attr.selectedValue?.value)
    }, [variationAttributes])

    const allVariationsSelected = useMemo(() => {
        return (
            variationAttributes.length > 0 &&
            variationAttributes.every((attr) => attr.selectedValue?.value)
        )
    }, [variationAttributes])

    const {data: basket} = useCurrentBasket()
    const MAX_PER_PRODUCT = product?.c_productMaxOrderQty || 100
    const productInBasket = basket?.productItems?.find(
        (item) => item.productId === (effectiveVariant?.productId || product?.id)
    )
    const quantityInBasket = productInBasket?.quantity || 0
    const hasReachedMaxQuantity = quantityInBasket >= MAX_PER_PRODUCT

    const cartInventoryMessage = useMemo(() => {
        if (hasReachedMaxQuantity) {
            return intl.formatMessage({
                defaultMessage: 'Maximum quantity has been reached',
                id: 'product_view.error.not_added_to_cart'
            })
        }
        return ''
    }, [hasReachedMaxQuantity, intl])

    const disableAddToCart = useMemo(() => {
        if (hasUnselectedVariations) return false
        if (allVariationsSelected && !effectiveVariant) return true
        if (effectiveVariant && !effectiveVariant.orderable) return true
        if (hasReachedMaxQuantity) return true
        return false
    }, [hasUnselectedVariations, allVariationsSelected, effectiveVariant, hasReachedMaxQuantity])

    // Get initial slide index for selected variation
    const getInitialSlideIndex = useCallback((values, selectedValue, attributeId) => {
        if (!selectedValue) return 0

        // Count only rendered slides (same filter logic as render)
        let renderedIndex = -1
        let currentRenderIndex = 0

        for (let i = 0; i < values.length; i++) {
            const val = values[i]

            // Skip items that won't be rendered (same logic as in JSX)
            if (attributeId === 'color' && val.online === false) {
                continue
            }

            // If this is our selected item, save the rendered index
            if (val.value === selectedValue.value) {
                renderedIndex = currentRenderIndex
                break
            }

            currentRenderIndex++
        }

        return renderedIndex - 1 !== -1 ? renderedIndex - 1 : 0
    }, [])

    useEffect(() => {
        if (!hasUnselectedVariations) {
            setShowOptionsMessage(false)
        }
    }, [hasUnselectedVariations])

    // Handle swatch selection changes - same logic as PDP but using local state instead of URL
    const handleSwatchChange = useCallback(
        (attributeId, value) => {
            setShowOptionsMessage(false)

            // Update local selections first (this triggers re-render with new selection)
            setLocalSelections((prev) => ({
                ...prev,
                [attributeId]: value
            }))

            // Build new variation params with the selected value
            const newVariationParams = {
                ...variationParams,
                [attributeId]: value
            }

            // For color changes, update pid to trigger product refetch (same as PDP navigation)
            if (attributeId === 'color') {
                const foundVariant = variants?.find(
                    (variantGroup) => variantGroup.variationValues?.color === value
                )

                if (foundVariant?.productId) {
                    setPid(foundVariant.productId)

                    if (setProductId) {
                        setProductId(foundVariant.productId)
                    }
                    if (setColorValue) {
                        setColorValue(value)
                    }
                }
                // Clear stored selections for the old pid; new color has no size selected yet
                clearQVSelections(pid)
                // Notify tile synchronously to clear size/length from productUrl on color change
                if (onLocalSelectionsChange) {
                    onLocalSelectionsChange({})
                }
            } else {
                // Synchronously persist to localStorage so PDP can restore selections
                // regardless of navigation timing
                const newNonColorSelections = Object.fromEntries(
                    Object.entries({...localSelections, [attributeId]: value}).filter(
                        ([key]) => key !== 'color'
                    )
                )
                setQVSelections(pid, newNonColorSelections)
                // Notify tile synchronously (same React batch) so productUrl is updated
                // before user clicks the link — avoids the one-render-cycle lag of useEffect
                if (onLocalSelectionsChange) {
                    onLocalSelectionsChange(newNonColorSelections)
                }
                // For non-color attributes, keep pid stable to avoid losing selected state
                // (variant pid switches remove variation attributes from the UI)
            }
        },
        [variationParams, variationAttributes, product?.variants, variants, setProductId, setColorValue, pid, localSelections, onLocalSelectionsChange]
    )

    // Function to get the correct label for each attribute
    const getAttributeLabel = (attributeId, attributeName, selectedValue) => {
        if (attributeId === 'color') {
            // Only color shows dynamic label with selected value
            return selectedValue?.name || 'Please select a color'
        }
        // All other attributes show static label (attribute name only)
        return attributeName
    }

    const missingAttributeMessages = useMemo(() => {
        const messages = {}

        ;(variationAttributes || []).forEach((attr) => {
            if (!attr.selectedValue?.value) {
                messages[attr.id] = intl.formatMessage(
                    {
                        defaultMessage: 'Please select a {attribute}',
                        id: 'product_view.error.missing_single_attribute'
                    },
                    {attribute: attr.name.toLowerCase()}
                )
            }
        })

        return messages
    }, [variationAttributes, intl])

    const resetSelections = useCallback(() => {
        const cacheKey = productId || pid
        if (cacheKey) {
            clearQVSelections(cacheKey)
        }
        forceResetSelectionsRef.current = true
        setLocalSelections({})
    }, [productId, pid])

    return {
        product,
        variant: effectiveVariant,
        variants,
        quantity,
        variationParams,
        variationAttributes,
        priceData,
        showSkeleton,
        showOptionsMessage,
        setShowOptionsMessage,
        missingAttributeMessages,
        handleSwatchChange,
        getInitialSlideIndex,
        getAttributeLabel,
        hasUnselectedVariations,
        allVariationsSelected,
        disableAddToCart,
        cartInventoryMessage,
        isVariationDataLoading,
        isAvailabilityResolved,
        isAvailabilityPending,
        isWishlistEnabled,
        resetSelections
    }
}

export default useQuickViewProductData
